/**
 * Organism and cell type definitions from PRD Section 3
 */

import type { HSLColor, Vector2, CellType } from './game.types';

// Organelle Types (PRD 3.2.3)
export enum OrganelleType {
  MITOCHONDRIA = 'MITOCHONDRIA',
  RIBOSOME = 'RIBOSOME',
  ENDOPLASMIC_RETICULUM = 'ENDOPLASMIC_RETICULUM',
  CHLOROPLAST = 'CHLOROPLAST',
  VACUOLE = 'VACUOLE',
  CENTRAL_VACUOLE = 'CENTRAL_VACUOLE',
  CILIA = 'CILIA',
  ORAL_GROOVE = 'ORAL_GROOVE',
  CONTRACTILE_VACUOLE = 'CONTRACTILE_VACUOLE',
  FOOD_VACUOLE = 'FOOD_VACUOLE',
  NUCLEOID = 'NUCLEOID',
}

// Organelle Configuration
export interface OrganelleConfig {
  type: OrganelleType;
  density?: number; // 0-1, how many relative to default
}

export interface OrganelleTemplate {
  shape: 'elongated-oval' | 'small-dot' | 'network' | 'lens-oval' | 'large-irregular';
  innerStructure: 'cristae-folds' | 'none' | 'tubular-channels' | 'thylakoid-stacks' | 'fluid-fill';
  color: HSLColor;
  size: { min: number; max: number };
  count: { min: number; max: number };
  animation: 'subtle-drift' | 'brownian' | 'slow-undulate' | 'slow-rotate' | 'slow-pulse';
  placement: 'cytoplasm-distributed' | 'rough-er-attached' | 'nucleus-adjacent' | 'center-or-distributed';
}

// Membrane Configuration (PRD 3.2.1)
export interface MembraneConfig {
  baseRadius: number;
  irregularity: number; // 0 = perfect circle, 1 = amoeba-like
  thickness: number;
  color: HSLColor;
  pulseRate: number;
  permeability: number;
}

// Nucleus Configuration
export interface NucleusConfig {
  relativeSize: number; // 0.1 to 0.4 of cell
  position: 'center' | 'offset';
  nucleolusCount: number;
  chromatinDensity: number;
}

// Cytoplasm Configuration
export interface CytoplasmConfig {
  density: number;
  color: HSLColor;
}

// Cell Animation Configuration
export interface CellAnimationConfig {
  drift?: number;
  rotate?: number;
  pulse?: number;
  morph?: number;
  cilia?: number;
  chloroplastStream?: number;
}

// Cell Shape Type
export type CellShape = 'spherical' | 'rod' | 'spiral';

// Procedural Cell Configuration (PRD 3.2.1)
export interface ProceduralCellConfig {
  cellType: CellType;
  shape?: CellShape; // spherical (coccus), rod (bacillus), spiral (spirillum)
  size: { min: number; max: number };
  membrane: MembraneConfig;
  nucleus: NucleusConfig | null; // null for prokaryotes
  organelles: OrganelleConfig[];
  cytoplasm: CytoplasmConfig;
  animation: CellAnimationConfig;
}

// Movement Patterns
export type MovementType =
  | 'bacterial-tumble'
  | 'amoeboid'
  | 'ciliary-glide'
  | 'static'
  | 'flagellar';

export interface MovementPattern {
  type: MovementType;
  speed: number;
}

// Cell Arrangement for Multicellular (PRD 3.3.2)
export type ArrangementPattern = 'packed-hexagonal' | 'layered' | 'branching';
export type TissueType = 'epidermis' | 'mesophyll' | 'vascular' | 'epithelial' | 'muscle' | 'connective';

export interface CellArrangement {
  pattern: ArrangementPattern;
  cellCount: { min: number; max: number };
  layerTypes?: TissueType[];
}

// Life Function Display
export type LifeFunctionDisplay =
  | 'binary-fission'
  | 'metabolism'
  | 'response'
  | 'phagocytosis'
  | 'photosynthesis'
  | 'transpiration'
  | 'growth'
  | 'filter-feeding'
  | 'conjugation';

// Organism Template (PRD 3.3.1)
export interface OrganismTemplate {
  id: string;
  name: string;
  type: 'unicellular' | 'multicellular';
  domain: 'bacteria' | 'archaea' | 'eukarya';
  kingdom?: 'animalia' | 'plantae' | 'fungi' | 'protista';
  cellConfig: ProceduralCellConfig;
  cellArrangement?: CellArrangement;
  colorVariation: number;
  sizeVariation: number;
  shapeVariation: number;
  movement: MovementPattern;
  lifeFunctions: LifeFunctionDisplay[];
}

// Non-Living Specimen (PRD 3.4)
export interface NonLivingTemplate {
  id: string;
  name: string;
  confusingFeatures: string[];
  distinguishingFeatures: string[];
}

// Placed Cell (for Builder mode)
export interface PlacedCell {
  x: number;
  y: number;
  size: number;
  type: CellType;
  config: ProceduralCellConfig;
}

// Rendered Cell Reference
export interface RenderedCell {
  id: string;
  container: Phaser.GameObjects.Container;
  config: ProceduralCellConfig;
  position: Vector2;
  animationState: CellAnimationState;
}

export interface CellAnimationState {
  pulsePhase: number;
  morphPhase: number;
  driftOffset: Vector2;
}
